import { OperationVariables, useApolloClient } from '@apollo/client';
import { create } from '@thoughtspot/logger';
import { DocumentNode } from 'graphql';
import React from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

const logger = create('Apollo-Cache-Hooks');

/**
 * Custom hook that provides cache watching functionality with initial cache read
 * This hook abstracts the pattern of:
 * 1. Reading initial data from cache
 * 2. Setting up cache.watch subscription
 * 3. Cleaning up subscription on unmount
 *
 * @param query - GraphQL DocumentNode to watch
 * @param variables - Variables for the query
 * @param onCacheDiff - Optional callback invoked with every cache result, from the
 *   initial read and from the cache.watch callback. Unlike the returned `data`
 *   (which is useState-backed and therefore coalesces intermediate values under
 *   React 18 automatic batching), this fires once per Apollo cache write, so a
 *   consumer that must observe every transition can do so here without dropping any.
 * @returns [data, setData] - Current data and setter function
 */
export function useCacheWatch<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables
>(
    query: DocumentNode,
    variables: TVariables,
    onCacheDiff?: (data: TData) => void,
): [TData | null, React.Dispatch<React.SetStateAction<TData | null>>] {
    const client = useApolloClient();
    const [data, setData] = React.useState<TData | null>(null);

    // Keep the latest callback without re-establishing the subscription each render.
    const onCacheDiffRef = React.useRef(onCacheDiff);
    onCacheDiffRef.current = onCacheDiff;

    // The cache.watch callback runs synchronously inside Apollo's
    // broadcastWatches, which itself runs inside whatever cache write
    // triggered it — often another mutation's markMutationResult. An
    // exception thrown by the consumer would propagate into that mutation's
    // promise chain, rejecting an unrelated mutation and silencing its
    // onCompleted/update callbacks. Never let a consumer throw escape.
    // Because the throw is logged and swallowed (not re-raised to any error
    // boundary), consumers must leave their own refs/bookkeeping in a
    // consistent state even when they throw mid-way — corruption here is
    // otherwise invisible beyond the ERROR log.
    const safeNotify = React.useCallback((result: TData) => {
        try {
            onCacheDiffRef.current?.(result);
        } catch (error) {
            logger.error('onCacheDiff consumer threw; swallowed to protect the cache broadcast', error);
        }
    }, []);

    useDeepCompareEffect(() => {
        try {
            const result = client.readQuery<TData, TVariables>({
                query,
                variables,
            });
            setData(result as TData);
            if (result) {
                safeNotify(result as TData);
            }
        } catch (error) {
            // Data not in cache yet
            setData(null);
        }

        // Watch for cache changes - re-establish when dependencies change
        const subscription = client.cache.watch({
            query,
            variables,
            optimistic: false,
            callback: (diff: { result?: TData }) => {
                if (diff.result) {
                    // Notify the per-write consumer before setData, so refs are
                    // updated regardless of how React batches the resulting render.
                    safeNotify(diff.result);
                    setData(diff.result);
                }
            },
        });

        return () => {
            subscription();
        };
    }, [client, query, variables]);

    return [data, setData];
}

/**
 * Custom hook specifically for Answer session cache watching
 * Provides a more specific interface for Answer-related queries
 *
 * @param query - GraphQL DocumentNode to watch
 * @param answerSessionId - Answer session ID
 * @returns [data, setData] - Current data and setter function
 */
export function useAnswerSessionCacheWatch<TData = unknown>(
    query: DocumentNode,
    answerSessionId: string,
): [TData | null, React.Dispatch<React.SetStateAction<TData | null>>] {
    return useCacheWatch<TData, { session: { sessionId: string } }>(query, {
        session: { sessionId: answerSessionId },
    });
}
